import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  MessageSquare, 
  ArrowLeft, 
  Calendar,
  ChevronRight,
  Loader2,
  Search,
  CheckCircle,
  Clock,
  TrendingUp,
  Zap,
  HelpCircle,
  Users,
  MessageCircle,
  GitBranch,
  Target,
  BarChart3,
  Plus,
  Filter
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map situation types to icons
const TYPE_ICONS = {
  stakeholder_dynamics: Zap,
  expectation_clarity: HelpCircle,
  team_friction: Users,
  difficult_conversation: MessageSquare,
  decision_making: GitBranch,
  execution_gap: Target,
  career_uncertainty: TrendingUp,
  time_pressure: Clock,
  upward_management: MessageCircle,
  credit_visibility: BarChart3,
  general: MessageSquare
};

const SituationsPage = () => {
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [situations, setSituations] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch situations and patterns in parallel
        const [situationsRes, patternsRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/situations`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/dashboard/situations/patterns?days=30`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (!situationsRes.ok || !patternsRes.ok) {
          throw new Error('Failed to load data');
        }

        const situationsData = await situationsRes.json();
        const patternsData = await patternsRes.json();

        setSituations(situationsData.situations || []);
        setPatterns(patternsData);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      loadData();
    }
  }, [isAuthenticated, token, navigate]);

  // Filter situations
  const filteredSituations = situations.filter(situation => {
    // Status filter
    if (statusFilter !== 'all' && situation.status !== statusFilter) {
      return false;
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (situation.initial_message || '').toLowerCase().includes(query) ||
        (situation.classification?.primary_type_name || '').toLowerCase().includes(query)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="situations-back-link"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">My Situations</h1>
              <p className="text-sm text-zinc-400">Track and reflect on work challenges</p>
            </div>
          </div>
          <Link to="/clarity">
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="new-situation-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Situation
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Patterns Overview */}
        {patterns && patterns.total_situations > 0 && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Situations */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-400 text-sm">Total Situations</span>
                <MessageSquare className="w-5 h-5 text-zinc-600" />
              </div>
              <p className="text-3xl font-bold text-white">{patterns.total_situations}</p>
              <p className="text-xs text-zinc-500 mt-1">Last {patterns.period_days} days</p>
            </div>

            {/* Top Pattern */}
            {patterns.type_distribution && patterns.type_distribution[0] && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-400 text-sm">Most Common</span>
                  <TrendingUp className="w-5 h-5 text-red-500" />
                </div>
                <p className="text-lg font-semibold text-white">
                  {patterns.type_distribution[0].type_name}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {patterns.type_distribution[0].count} situations ({patterns.type_distribution[0].percentage}%)
                </p>
              </div>
            )}

            {/* Resolution Rate */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-400 text-sm">Resolution Rate</span>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-3xl font-bold text-white">
                {patterns.total_situations > 0 
                  ? Math.round((patterns.status_counts?.resolved || 0) / patterns.total_situations * 100)
                  : 0}%
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {patterns.status_counts?.resolved || 0} resolved, {patterns.status_counts?.open || 0} open
              </p>
            </div>
          </div>
        )}

        {/* Insights */}
        {patterns?.insights && patterns.insights.length > 0 && (
          <div className="mb-8">
            {patterns.insights.map((insight, idx) => (
              <div 
                key={idx}
                className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-3"
              >
                <div className="flex items-start gap-3">
                  <BarChart3 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-medium">{insight.message}</p>
                    <p className="text-amber-400/70 text-sm mt-1">{insight.suggestion}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Search situations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="situations-search"
              className="w-full pl-10 pr-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="situations-status-filter"
              className="bg-zinc-900 border border-zinc-800 rounded-lg text-white px-3 py-2.5 focus:outline-none focus:border-red-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {/* Situations List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
          </div>
        ) : filteredSituations.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No situations yet</h3>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Start by describing a tricky work situation. We'll help you think it through.
            </p>
            <Link to="/clarity">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Describe a Situation
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSituations.map((situation) => {
              const TypeIcon = TYPE_ICONS[situation.classification?.primary_type] || MessageSquare;
              const isResolved = situation.status === 'resolved';
              
              return (
                <div
                  key={situation.id}
                  className={`bg-zinc-900 border rounded-xl p-4 hover:border-zinc-700 transition-colors ${
                    isResolved ? 'border-zinc-800/50 opacity-75' : 'border-zinc-800'
                  }`}
                  data-testid={`situation-card-${situation.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isResolved ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}>
                      {isResolved ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <TypeIcon className="w-5 h-5 text-red-500" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="secondary"
                          className="text-xs bg-zinc-800 text-zinc-300"
                        >
                          {situation.classification?.primary_type_name || 'General'}
                        </Badge>
                        {isResolved && (
                          <Badge className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                            Resolved
                          </Badge>
                        )}
                        {situation.source?.startsWith('auto_detected') && (
                          <Badge className="text-xs bg-purple-500/20 text-purple-400 border-purple-500/30">
                            Auto-detected
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-white font-medium line-clamp-2 mb-2">
                        {situation.initial_message}
                      </p>

                      {/* Summary if available */}
                      {situation.summary?.title && (
                        <p className="text-sm text-zinc-400 mb-2">
                          {situation.summary.title}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(situation.created_at)}
                        </span>
                        {situation.source && (
                          <span className="capitalize">{situation.source.replace('_', ' ')}</span>
                        )}
                      </div>
                    </div>

                    {/* Action */}
                    <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default SituationsPage;
